
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

interface CookiePreferencesModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSavePreferences: (preferences: { analytics: boolean; marketing: boolean; essential: boolean }) => void;
}

const CookiePreferencesModal = ({ isOpen, onClose, onSavePreferences }: CookiePreferencesModalProps) => {
    const [analytics, setAnalytics] = useState(false);
    const [marketing, setMarketing] = useState(false);
    const [essential, setEssential] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);

    // Load current preferences when modal opens with performance optimization
    useEffect(() => {
        if (isOpen) {
            // Use requestAnimationFrame to avoid blocking the main thread
            const timer = requestAnimationFrame(() => {
                try {
                    const stored = localStorage.getItem('cookiePreferences');
                    if (stored) {
                        const prefs = JSON.parse(stored);
                        setAnalytics(prefs.analytics || false);
                        setMarketing(prefs.marketing || false);
                        // Essential is always true by default, only false if explicitly denied
                        setEssential(prefs.essential !== false);
                    }
                    setIsLoaded(true);
                } catch (err) {
                    console.error('Error loading cookie preferences:', err);
                    setIsLoaded(true);
                }
            });
            
            return () => cancelAnimationFrame(timer);
        }
    }, [isOpen]);

    const handleDeny = () => {
        setAnalytics(false);
        setMarketing(false);
        setEssential(false); // Special case: essential becomes false
        // Clear localStorage so popup shows again
        localStorage.removeItem('cookieConsent');
        localStorage.removeItem('cookiePreferences');
        onSavePreferences({ analytics: false, marketing: false, essential: false });
        // Dispatch custom event to notify ClientScripts
        window.dispatchEvent(new CustomEvent('cookiePreferencesChanged'));
        onClose();
    };

    const handleAllowSelection = () => {
        // Essential is always true for allow selection (user can't disable it)
        onSavePreferences({ analytics, marketing, essential: true });
        // Dispatch custom event to notify ClientScripts
        window.dispatchEvent(new CustomEvent('cookiePreferencesChanged'));
        onClose();
    };

    const handleAllowAll = () => {
        setAnalytics(true);
        setMarketing(true);
        setEssential(true);
        onSavePreferences({ analytics: true, marketing: true, essential: true });
        // Dispatch custom event to notify ClientScripts
        window.dispatchEvent(new CustomEvent('cookiePreferencesChanged'));
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent 
                id="cookie-preferences-dialog" 
                className="md:h-[400px] md:w-[400px]"
            >
                <DialogHeader>
                    <DialogTitle id="cookie-preferences-title">Cookie Preferences</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6">
                    <div id="cookie-preferences-description" className="text-sm text-muted-foreground">
                        Manage your cookie settings. You can enable or disable different types of cookies below.
                    </div>

                    {!isLoaded ? (
                        // Loading state to prevent layout shift
                        <div className="space-y-4">
                            <div className="animate-pulse">
                                <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                                <div className="h-3 bg-muted rounded w-1/2"></div>
                            </div>
                            <div className="animate-pulse">
                                <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                                <div className="h-3 bg-muted rounded w-1/2"></div>
                            </div>
                            <div className="animate-pulse">
                                <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                                <div className="h-3 bg-muted rounded w-1/2"></div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Essential & Preferences - Always enabled */}
                            <div className="flex items-center justify-between space-x-3">
                                <div className="flex-1">
                                    <div className="font-medium text-sm">Essential & Preferences</div>
                                    <div className="text-xs text-muted-foreground">
                                        Required for basic site functionality and support widget
                                    </div>
                                </div>
                                <Switch
                                    id="cookie-preferences-essential"
                                    checked={essential}
                                    disabled={true}
                                    className="opacity-50"
                                />
                            </div>

                            {/* Analytics */}
                            <div className="flex items-center justify-between space-x-3">
                                <div className="flex-1">
                                    <div className="font-medium text-sm">Analytics</div>
                                    <div className="text-xs text-muted-foreground">
                                        Help us understand how visitors interact with our website
                                    </div>
                                </div>
                                <Switch
                                    id="cookie-preferences-analytics"
                                    checked={analytics}
                                    onCheckedChange={setAnalytics}
                                />
                            </div>

                            {/* Marketing */}
                            <div className="flex items-center justify-between space-x-3">
                                <div className="flex-1">
                                    <div className="font-medium text-sm">Marketing</div>
                                    <div className="text-xs text-muted-foreground">
                                        Used to track visitors and show relevant advertisements
                                    </div>
                                </div>
                                <Switch
                                    id="cookie-preferences-marketing"
                                    checked={marketing}
                                    onCheckedChange={setMarketing}
                                />
                            </div>
                        </div>
                    )}

                    <div className="flex gap-2 pt-4">
                        <Button
                            id="cookie-preferences-deny"
                            onClick={handleDeny}
                            variant="outline"
                            size="sm"
                            className="flex-1"
                        >
                            Deny
                        </Button>
                        <Button
                            id="cookie-preferences-allow-selection"
                            onClick={handleAllowSelection}
                            variant="outline"
                            size="sm"
                            className="flex-1"
                        >
                            Allow Selection
                        </Button>
                        <Button
                            id="cookie-preferences-allow-all"
                            onClick={handleAllowAll}
                            size="sm"
                            className="flex-1"
                        >
                            Allow All
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default CookiePreferencesModal;